import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { X, ChevronLeft, ChevronRight, Users, MapPin } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom price bubble marker
const createPriceBubble = (price, count = null, isHovered = false) => {
  const displayText = count ? `${count}` : `${Math.round(price)}€`;
  const width = count ? 36 : Math.max(50, displayText.length * 10 + 20);
  
  return L.divIcon({
    className: 'custom-price-marker',
    html: `
      <div style="
        background: ${isHovered ? 'var(--color-primary, #1a1a1a)' : 'white'};
        color: ${isHovered ? 'white' : 'var(--color-text-primary, #1a1a1a)'};
        padding: ${count ? '8px' : '6px 12px'};
        border-radius: ${count ? '50%' : '20px'};
        font-weight: 600;
        font-size: ${count ? '14px' : '13px'};
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        border: 2px solid ${isHovered ? 'var(--color-primary, #1a1a1a)' : 'white'};
        white-space: nowrap;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: ${count ? '36px' : 'auto'};
        height: ${count ? '36px' : 'auto'};
        transition: all 0.2s ease;
        cursor: pointer;
      ">
        ${displayText}
      </div>
    `,
    iconSize: [width, 36],
    iconAnchor: [width / 2, 36],
    popupAnchor: [0, -40],
  });
};

// Component to fit bounds when properties change
const FitBounds = ({ properties }) => {
  const map = useMap();
  const fittedRef = useRef(false);
  
  useEffect(() => {
    // Only fit bounds once on initial load
    if (fittedRef.current) return;
    
    if (properties.length > 0) {
      const validLocations = properties.filter(p => p.latitude && p.longitude);
      if (validLocations.length > 0) {
        const bounds = L.latLngBounds(
          validLocations.map(p => [p.latitude, p.longitude])
        );
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
        fittedRef.current = true;
      }
    }
  }, [properties, map]);
  
  return null;
};

// Room carousel for popup with slide animation
const RoomCarousel = ({ rooms, property, onSelectRoom, checkIn, checkOut }) => {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  
  const nextSlide = (e) => {
    e.stopPropagation();
    if (isAnimating) return;
    setIsAnimating(true);
    setSlideDirection('slide-left');
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % rooms.length);
      setSlideDirection('');
      setIsAnimating(false);
    }, 200);
  };
  
  const prevSlide = (e) => {
    e.stopPropagation();
    if (isAnimating) return;
    setIsAnimating(true);
    setSlideDirection('slide-right');
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + rooms.length) % rooms.length);
      setSlideDirection('');
      setIsAnimating(false);
    }, 200);
  };
  
  const room = rooms[currentIndex];
  
  // Get border radius from CSS variable
  const borderRadius = getComputedStyle(document.documentElement).getPropertyValue('--border-radius').trim() || '8px';
  
  // Get image URL - check multiple possible sources
  const getImageUrl = () => {
    // Check room's own images array
    if (room.images && room.images.length > 0) {
      return room.images[0];
    }
    // Check room's image field
    if (room.image) {
      return room.image;
    }
    // Check property's images
    if (room.propertyRef?.images && room.propertyRef.images.length > 0) {
      return room.propertyRef.images[0];
    }
    // Check media array (Beds24 format)
    if (room.media && room.media.length > 0) {
      const firstMedia = room.media[0];
      return firstMedia.url || firstMedia.imageUrl || firstMedia;
    }
    // Fallback
    return 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=400';
  };
  
  const imageUrl = getImageUrl();
  
  // Get max guests - check multiple possible field names
  const maxGuests = room.maxPeople || room.maxGuests || room.max_guests || room.qty || '-';
  
  // Calculate price per night from total price with best discount
  const calculatePricePerNight = () => {
    if (!room.price) return null;
    
    let pricePerNight = room.price;
    
    // If we have check-in and check-out dates, calculate nights
    if (checkIn && checkOut) {
      const checkInDate = new Date(checkIn);
      const checkOutDate = new Date(checkOut);
      const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
      if (nights > 0) {
        pricePerNight = room.price / nights;
        
        // Apply best available discount from rates if room has them
        if (room.rates && room.rates.length > 0) {
          let bestDiscount = 0;
          room.rates.forEach(rate => {
            const minNights = rate.min_nights || 1;
            const maxNights = rate.max_nights || Infinity;
            if (nights >= minNights && nights <= maxNights) {
              if (rate.discount_percent > bestDiscount) {
                bestDiscount = rate.discount_percent;
              }
            }
          });
          if (bestDiscount > 0) {
            pricePerNight = pricePerNight * (1 - bestDiscount / 100);
          }
        }
      }
    }
    
    // If pricePerNight is available, use it
    if (room.pricePerNight) {
      return Math.round(room.pricePerNight);
    }
    
    return Math.round(pricePerNight);
  };
  
  const pricePerNight = calculatePricePerNight();
  const hasMultipleRooms = rooms.length > 1;
  
  return (
    <div className="flex items-center gap-2">
      {/* Left arrow - no shadow/background wrapper */}
      {hasMultipleRooms && (
        <button 
          onClick={prevSlide}
          className="flex-shrink-0 bg-white hover:bg-gray-50 w-9 h-9 flex items-center justify-center rounded-full shadow-sm border border-gray-200 transition-all hover:scale-105 z-10"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
        >
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>
      )}
      
      {/* Card content with slide animation - use theme border radius */}
      <div 
        className={`w-64 overflow-hidden bg-white ${slideDirection}`} 
        style={{ 
          transition: 'transform 0.2s ease-out',
          borderRadius: borderRadius
        }}
      >
        {/* Image */}
        <div 
          className="relative h-36 overflow-hidden"
          style={{ borderTopLeftRadius: borderRadius, borderTopRightRadius: borderRadius }}
        >
          <img 
            src={imageUrl} 
            alt={room.name} 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=400';
            }}
          />
        </div>
        
        {/* Content */}
        <div 
          className="p-3 bg-white"
          style={{ borderBottomLeftRadius: borderRadius, borderBottomRightRadius: borderRadius }}
        >
          <h4 className="font-semibold text-sm text-gray-900 mb-1 truncate">{room.name}</h4>
          <p className="text-xs text-gray-500 mb-2 truncate">{property.name || property.property_name}</p>
          
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <Users className="w-3.5 h-3.5" />
              <span>{t('room_card.max_guests')}: {maxGuests}</span>
            </div>
            <div className="text-right">
              <span className="text-sm font-bold text-[var(--color-primary)]">
                {pricePerNight ? `€${pricePerNight}` : '-'}
              </span>
              <span className="text-xs text-gray-500">/{t('room_card.night')}</span>
            </div>
          </div>
          
          <button
            onClick={() => onSelectRoom(property, room)}
            className="w-full py-2 px-3 bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity"
            style={{ borderRadius: borderRadius }}
          >
            {t('map.viewDetails')}
          </button>
          
          {/* Dots indicator - below button */}
          {hasMultipleRooms && (
            <div className="flex justify-center gap-1.5 mt-3 pt-2 border-t border-gray-100">
              {rooms.map((_, idx) => (
                <button 
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex(idx);
                  }}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentIndex 
                      ? 'bg-[var(--color-primary)]' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Right arrow - no shadow/background wrapper */}
      {hasMultipleRooms && (
        <button 
          onClick={nextSlide}
          className="flex-shrink-0 bg-white hover:bg-gray-50 w-9 h-9 flex items-center justify-center rounded-full shadow-sm border border-gray-200 transition-all hover:scale-105 z-10"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
        >
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>
      )}
    </div>
  );
};

// Map invalidation handler for fullscreen mode
const MapInvalidator = ({ isFullscreen }) => {
  const map = useMap();
  
  useEffect(() => {
    // Invalidate map size when fullscreen changes
    if (map) {
      setTimeout(() => {
        map.invalidateSize();
      }, 100);
    }
  }, [isFullscreen, map]);
  
  return null;
};

// Main map component
const PropertyMap = ({ 
  properties, 
  onSelectRoom, 
  isFullscreen = false, 
  onClose = null,
  className = '',
  checkIn = null,
  checkOut = null
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [hoveredProperty, setHoveredProperty] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef(null);
  
  // Handle map ready state
  useEffect(() => {
    // Small delay to ensure container is rendered
    const timer = setTimeout(() => {
      setMapReady(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  
  // Calculate price per night for marker bubble (with best discount applied)
  const calculateMinPricePerNight = React.useCallback((rooms) => {
    if (!rooms || rooms.length === 0) return Infinity;
    
    let minPrice = Infinity;
    
    rooms.forEach(room => {
      if (!room.price) return;
      
      let pricePerNight = room.price;
      
      // Calculate per night if we have dates
      if (checkIn && checkOut) {
        const checkInDate = new Date(checkIn);
        const checkOutDate = new Date(checkOut);
        const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
        if (nights > 0) {
          pricePerNight = room.price / nights;
        }
        
        // Apply best available discount from rates if room has them
        if (room.rates && room.rates.length > 0) {
          let bestDiscount = 0;
          room.rates.forEach(rate => {
            const minNights = rate.min_nights || 1;
            const maxNights = rate.max_nights || Infinity;
            if (nights >= minNights && nights <= maxNights) {
              if (rate.discount_percent > bestDiscount) {
                bestDiscount = rate.discount_percent;
              }
            }
          });
          if (bestDiscount > 0) {
            pricePerNight = pricePerNight * (1 - bestDiscount / 100);
          }
        }
      }
      
      if (pricePerNight < minPrice) {
        minPrice = pricePerNight;
      }
    });
    
    return minPrice;
  }, [checkIn, checkOut]);
  
  // Group properties by location (same coordinates)
  const groupedProperties = React.useMemo(() => {
    const groups = {};
    
    properties.forEach(property => {
      if (!property.latitude || !property.longitude) return;
      
      const key = `${property.latitude.toFixed(5)}_${property.longitude.toFixed(5)}`;
      if (!groups[key]) {
        groups[key] = {
          latitude: property.latitude,
          longitude: property.longitude,
          properties: [],
          rooms: []
        };
      }
      groups[key].properties.push(property);
      
      // Collect all rooms from this property
      if (property.available_rooms) {
        property.available_rooms.forEach(room => {
          groups[key].rooms.push({ 
            ...room, 
            propertyRef: property,
            // Copy property images if room doesn't have its own
            images: room.images || room.media?.map(m => m.url || m) || property.images
          });
        });
      }
    });
    
    // Calculate min price per night for each group
    Object.values(groups).forEach(group => {
      group.minPricePerNight = calculateMinPricePerNight(group.rooms);
    });
    
    return Object.values(groups);
  }, [properties, checkIn, checkOut, calculateMinPricePerNight]);
  
  // Default center (Croatia - Rijeka area)
  const defaultCenter = [45.3271, 14.4422];
  const defaultZoom = 12;
  
  // Check if we have any valid locations
  const hasLocations = groupedProperties.length > 0;
  
  if (!hasLocations) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <div className="text-center p-8">
          <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">{t('map.noLocations')}</p>
        </div>
      </div>
    );
  }
  
  // Don't render map until ready (prevents Leaflet errors on mobile)
  if (!mapReady) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`}>
        <div className="animate-spin h-8 w-8 border-2 border-[var(--color-primary)] border-r-transparent rounded-full"></div>
      </div>
    );
  }
  
  return (
    <div className={`relative ${className}`}>
      {/* Fullscreen close button */}
      {isFullscreen && onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-[1000] bg-white p-2 rounded-full shadow-lg hover:bg-gray-100 transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
      )}
      
      <MapContainer
        ref={mapRef}
        center={defaultCenter}
        zoom={defaultZoom}
        className="w-full h-full"
        style={{ minHeight: '400px' }}
        zoomControl={true}
        scrollWheelZoom={true}
        dragging={true}
        touchZoom={true}
        doubleClickZoom={true}
        tap={false}
        whenReady={() => {
          // Invalidate size after map is ready
          if (mapRef.current) {
            setTimeout(() => {
              mapRef.current.invalidateSize();
            }, 200);
          }
        }}
      >
        {theme.carto_api_key ? (
          <TileLayer
            key="carto"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>'
            url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${encodeURIComponent(theme.carto_api_key.trim())}`}
          />
        ) : (
          <TileLayer
            key="osm"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        )}
        
        <FitBounds properties={properties.filter(p => p.latitude && p.longitude)} />
        <MapInvalidator isFullscreen={isFullscreen} />
        
        {groupedProperties.map((group, idx) => {
          const isCluster = group.rooms.length > 1;
          const isHovered = hoveredProperty === idx;
          const displayPrice = group.minPricePerNight === Infinity ? 0 : Math.round(group.minPricePerNight);
          
          return (
            <Marker
              key={idx}
              position={[group.latitude, group.longitude]}
              icon={createPriceBubble(
                displayPrice,
                isCluster ? group.rooms.length : null,
                isHovered
              )}
              eventHandlers={{
                mouseover: () => setHoveredProperty(idx),
                mouseout: () => setHoveredProperty(null),
              }}
            >
              <Popup className="property-popup" maxWidth={400} minWidth={280} autoPan={true}>
                <RoomCarousel
                  rooms={group.rooms}
                  property={group.properties[0]}
                  onSelectRoom={(prop, room) => {
                    const actualProperty = room.propertyRef || prop;
                    onSelectRoom(actualProperty, room);
                  }}
                  checkIn={checkIn}
                  checkOut={checkOut}
                />
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
};

export default PropertyMap;
